import './index.less';
import { useContext, useEffect, useRef, useState } from 'react';
import { ReactComponent as BigAddSvg } from '../icons/add.svg';
import { ReactComponent as DeleteSvg } from '@/global/icons/side-tool/delete.svg';
import { ReactComponent as EditSvg } from '@/global/icons/side-tool/edit.svg';
import { ReactComponent as UploadSvg } from '@/global/icons/side-tool/upload.svg';
import { ReactComponent as DownloadSvg } from '@/global/icons/side-tool/download.svg';
import TnMessage from '@/global/components/message';
import { Button, Form, Input, InputNumber, Modal, Select, Skeleton, Spin, Tooltip, Upload } from 'antd';
import classNames from 'classnames';
import MonixModal from '@/global/components/monix-modal';
import TnModal from '@/global/components/base-modal';
import LibLibMonitorParameterDetail from './detail';
import { GlobalLoadDisplacement } from '@/global/interface/lib-device/global-load-dispalcement';
import { createGlobalLoadDisplacement, deleteGlobalLoadDisplacement, downloadLoadDisplacementImportTemplate, getGlobalLoadDisplacementDetail, getGlobalLoadDisplacements, updateGlobalLoadDisplacement, uploadLoadDisplacementTemplate } from '@/api/lib/lib-load-displacement';
import LibConstitutiveRelationDetail from './detail';
import { UploadFileContext } from '@/App';


export default function ConstitutiveRelationPanel() {
    const [relations, setRelations] = useState<GlobalLoadDisplacement[]>([]) // 本构关系列表
    const [loading, setLoading] = useState<boolean>(false) // loading
    const [activeItem, setActiveItem] = useState<GlobalLoadDisplacement>(null) // 激活的项

    const addBtnRef = useRef(null); // 添加表单的ref
    const editBtnRef = useRef(null); // 编辑表单的ref
    const [addForm] = Form.useForm(); // 添加表单
    const [editForm] = Form.useForm(); // 编辑表单

    const [showAddModel, setShowAddModel] = useState<boolean>(false) // 添加弹窗标志
    const [addLoading, setAddLoading] = useState<boolean>(false) // 添加弹窗loading
    const [showEditModel, setShowEditModel] = useState<boolean>(false) // 编辑弹窗标志
    const [editLoading, setEditLoading] = useState<boolean>(false) // 编辑弹窗loading
    const [needRender, setNeedRender] = useState<boolean>(false) // 重新渲染

    const uploadRef = useRef(null); // 上传ref
    const uploadFileContext = useContext(UploadFileContext); // 上传上下文
    const [uploadFileList, setUploadFileList] = useState([]); // 上传文件列表

    // 初始化
    useEffect(() => {
        generateData(true);
    }, [])

    useEffect(() => {
        if (!showAddModel) {
            addForm.resetFields();
        }
    }, [showAddModel])

    useEffect(() => {
        if (!showEditModel) {
            editForm.resetFields();
        }
    }, [showEditModel])


    // 获取数据
    const generateData = (setActive = false) => {
        setLoading(true);
        getGlobalLoadDisplacements().then(res => {
            if (res.status === 200) {
                let data: GlobalLoadDisplacement[] = res.data || [];
                setRelations(data);
                if (setActive && data?.length > 0) {
                    setActiveItem(data[0]);
                }
            }
        }).catch(err => {
            TnMessage.error("获取本构关系失败");
        }).finally(() => {
            setLoading(false);
        })
    }

    // 点击工具栏添加按钮
    const onAddClick = () => {
        setShowAddModel(true);
    }

    // 点击工具栏编辑按钮
    const onEditClick = () => {
        if (!activeItem) {
            TnMessage.warning("未选中本构关系");
            return;
        }
        // 将信息注入
        setShowEditModel(true);
        setEditLoading(true);
        getGlobalLoadDisplacementDetail(activeItem?.uuid).then(res => {
            let data = res.data;
            editForm.setFieldsValue({ ...data });
        }).catch(err => {
            TnMessage.error("获取本构关系失败");
        }).finally(() => {
            setEditLoading(false);
        })
    }

    // 点击工具栏删除按钮
    const onDeleteClick = () => {
        if (!activeItem) {
            TnMessage.warning("未选中本构关系");
            return;
        }
        TnModal().deleteModal({
            onOk() {
                setLoading(true);
                Modal.destroyAll();
                deleteGlobalLoadDisplacement(activeItem?.uuid).then(res => {
                    if (res.status === 200) {
                        generateData(true);
                        TnMessage.success("删除成功");
                        setShowEditModel(false);
                        Modal.destroyAll();
                    }
                }).catch(err => {
                    TnMessage.error("删除失败");
                }).finally(() => {
                    setLoading(false);
                })
            },
            onCancel() {
                Modal.destroyAll();
            },
            closable: true,
        })
    }

    // 点击添加弹窗确认按钮
    const onAddSaveClick = () => {
        if (addLoading) return;
        if (addBtnRef.current) {
            addBtnRef.current.click();
        }
    }

    // 创建表单提交
    const onAddFinish = (values) => {
        setAddLoading(true);
        let data = { ...values };
        createGlobalLoadDisplacement(data).then(res => {
            if (res.status === 200) {
                generateData(false);
                let newItem: GlobalLoadDisplacement = {
                    id: res.data,
                    uuid: res.data,
                }
                setActiveItem(newItem);
                TnMessage.success("添加成功");
                setShowAddModel(false);
            }
        }).catch(err => {
            TnMessage.error(err.data || '添加失败');
        }).finally(() => {
            setAddLoading(false);
        });
    }

    // 点击编辑弹窗确认按钮
    const onEditSaveClick = () => {
        if (editLoading) return;
        if (editBtnRef.current) {
            editBtnRef.current.click();
        }
    }

    // 编辑表单提交
    const onEditFinish = (values) => {
        setEditLoading(true);
        let data = { ...values };
        updateGlobalLoadDisplacement(activeItem?.uuid, data).then(res => {
            if (res.status === 200) {
                generateData(false);
                setNeedRender(pre => !pre);
                TnMessage.success("编辑成功");
                setShowEditModel(false);
            }
        }).catch(err => {
            TnMessage.error(err?.data || "编辑失败");
        }).finally(() => {
            setEditLoading(false);
        });
    }

    // 点击下载按钮
    const onDownloadClick = () => {
        TnMessage.loading("正在下载导入模板...");
        downloadLoadDisplacementImportTemplate().then(res => {
            if (res.status === 200) {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                // 创建a标签，为了弹出下载框
                const link = document.createElement('a');
                link.style.display = 'none';
                // 设置a标签路径
                link.href = url;
                // 设置文件名
                link.download = '本构关系导入模板.xlsx';
                document.body.appendChild(link);
                link.click();
                // 释放 URL对象
                URL.revokeObjectURL(link.href);
                document.body.removeChild(link);
                TnMessage.destory();
            }
        }).catch(err => {
            TnMessage.destory();
            TnMessage.error("获取导入模板失败");
        })
    }

    const onUploadClick = () => {

    }

    const beforeUpload = (file) => {
        const isXlsx = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        if (!isXlsx) {
            TnMessage.warning('只能上传 xlsx 文件');
            return false;
        }
    }

    const uploadHeadXlsx = (info) => {
        let fileFormData = new FormData();
        let file: File = info.file;
        fileFormData.append('file', file);
        uploadLoadDisplacementTemplate(activeItem?.uuid, fileFormData).then(res => {
            if (res.data) {
                // generateData(false);
                setNeedRender(pre => !pre);
                TnMessage.success('导入成功');
            }
        }).catch(err => {
            TnMessage.success('导入失败');
        })
    }

    const handleChange = (info) => {
        if (info.file.status === 'done') {
            TnMessage.success(`${info.file.name} file uploaded successfully`);
        } else if (info.file.status === 'error') {
            TnMessage.error(`${info.file.name} file upload failed.`);
        }
    }

    return (
        <div className='lib-relation-container'>
            <div className="ope-header">
                <div className="create-relation-btn" onClick={onAddClick}><BigAddSvg />添加本构关系</div>
                <div className='ope-icon' onClick={onDownloadClick}><DownloadSvg /></div>
                <Upload
                    name='file'
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    customRequest={uploadHeadXlsx}
                    onChange={handleChange}
                >
                    <div className='ope-icon' style={{ marginTop: 3 }} onClick={onUploadClick}><UploadSvg /></div>
                </Upload>
                <div className='ope-icon' onClick={onEditClick}><EditSvg /></div>
                <div className='ope-icon' onClick={onDeleteClick}><DeleteSvg /></div>
            </div>
            <div className="lib-relation-panel">
                <Skeleton loading={loading}>
                    <div className="lib-relation-list">
                        <div className="lib-header">
                            <div className='header-cell-1'>类型</div>
                            <div className='header-cell-1'>名称</div>
                        </div>
                        <div className="lib-body">
                            {relations?.map((item: GlobalLoadDisplacement, key) => {
                                let showDivider = false;
                                let type = item?.type || '';
                                let name = item?.name || '';
                                if (key > 0) {
                                    if (relations[key]?.type === relations[key - 1]?.type) {
                                        type = '';
                                    }
                                    if (relations[key]?.type !== relations[key - 1]?.type) {
                                        showDivider = true;
                                    }
                                }
                                return (
                                    <>
                                        {showDivider && <div className='divider'></div>}
                                        <div className={classNames({
                                            "body-row": true,
                                            "body-row-active": activeItem?.uuid === item?.uuid,
                                        })} key={key} onClick={() => setActiveItem(item)}>
                                            <div className='body-cell-1'>{type}</div>
                                            <div className='body-cell-1'>{name}</div>
                                        </div>
                                    </>
                                )
                            })}
                        </div>
                    </div>
                </Skeleton>
                <div className="lib-relation-detail">
                    <div className='lib-header'>详情</div>
                    <LibConstitutiveRelationDetail relationUuid={activeItem?.uuid} needRender={needRender} />
                </div>
            </div>

            {/* <input type="file" ref={uploadRef} style={{ display: "none" }} onChange={handleFileChange} multiple /> */}

            <MonixModal
                title={"添加本构关系"}
                visible={showAddModel}
                setVisible={(e) => setShowAddModel(e)}
                onSaveClick={onAddSaveClick}
                wrapClassName="form-modal"
                saveDisable={addLoading}
            >
                <Spin wrapperClassName="form-body" spinning={addLoading}>
                    <Form
                        colon={false}
                        form={addForm}
                        autoComplete="off"
                        labelCol={{ span: 6 }}
                        onFinish={onAddFinish}
                    >
                        <Form.Item label="类型:" name="type" rules={[{ required: true, message: '类型不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="名称:" name="name" rules={[{ required: true, message: '名称不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Button ref={addBtnRef} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </Spin>
            </MonixModal>

            <MonixModal
                title={"编辑本构关系"}
                visible={showEditModel}
                setVisible={(e) => setShowEditModel(e)}
                onSaveClick={onEditSaveClick}
                wrapClassName="form-modal"
                saveDisable={editLoading}
            >
                <Spin wrapperClassName="form-body" spinning={editLoading}>
                    <Form
                        colon={false}
                        form={editForm}
                        autoComplete="off"
                        labelCol={{ span: 6 }}
                        onFinish={onEditFinish}
                    >
                        <Form.Item label="类型:" name="type" rules={[{ required: true, message: '类型不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="名称:" name="name" rules={[{ required: true, message: '名称不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Button ref={editBtnRef} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </Spin>
            </MonixModal>
        </div >
    )
}